import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_FILE_COUNT } from '../constants/fileUpload';

export interface FileValidationError {
  file: File;
  reason: string;
}

export interface FileValidationOptions {
  allowedFileTypes?: string[];
  maxFileSize?: number;
  maxFileCount?: number;
}

function resolveAllowedTypes(options?: FileValidationOptions) {
  if (options?.allowedFileTypes && options.allowedFileTypes.length > 0) {
    return new Set(options.allowedFileTypes);
  }
  return ALLOWED_FILE_TYPES;
}

function resolveMaxFileSize(options?: FileValidationOptions) {
  if (typeof options?.maxFileSize === 'number' && options.maxFileSize > 0) {
    return options.maxFileSize;
  }
  return MAX_FILE_SIZE;
}

function resolveMaxFileCount(options?: FileValidationOptions) {
  if (typeof options?.maxFileCount === 'number' && options.maxFileCount > 0) {
    return options.maxFileCount;
  }
  return MAX_FILE_COUNT;
}

export function validateFile(file: File, options?: FileValidationOptions): string | null {
  const allowedTypes = resolveAllowedTypes(options);
  const maxSize = resolveMaxFileSize(options);
  if (!allowedTypes.has(file.type)) {
    return `File type "${file.type || 'unknown'}" is not allowed. Accepted: ${Array.from(allowedTypes).join(', ')}.`;
  }
  if (file.size > maxSize) {
    const limitMb = Math.round((maxSize / (1024 * 1024)) * 10) / 10;
    return `File "${file.name}" exceeds the ${limitMb}MB size limit.`;
  }
  return null;
}

export function validateFiles(
  files: File[],
  existingCount = 0,
  options?: FileValidationOptions
): { valid: File[]; errors: FileValidationError[] } {
  const errors: FileValidationError[] = [];
  const valid: File[] = [];

  const maxCount = resolveMaxFileCount(options);
  const totalAllowed = maxCount - existingCount;
  if (totalAllowed <= 0) {
    files.forEach(file => errors.push({ file, reason: `Maximum of ${maxCount} files allowed.` }));
    return { valid, errors };
  }

  const toProcess = files.slice(0, totalAllowed);
  const overflow = files.slice(totalAllowed);

  overflow.forEach(file => errors.push({ file, reason: `Maximum of ${maxCount} files allowed.` }));

  toProcess.forEach(file => {
    const error = validateFile(file, options);
    if (error) {
      errors.push({ file, reason: error });
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}

/**
 * Validates that a URL is a safe image URL (http or https only).
 * Rejects javascript:, data:, and other potentially dangerous schemes.
 */
export function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}
